import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ChevronDown, ChevronUp, BookOpen, AlertTriangle, FileText, Play, ExternalLink } from 'lucide-react';
import PageWrapper from '../components/PageWrapper';
import UrgencyGuidance from '../components/UrgencyGuidance';
import { getAnatomyPage, getAllAnatomyPages } from '../data/anatomyPages';

const AnatomyPage = () => {
  const { organId } = useParams();
  const navigate = useNavigate();
  const [showMoreDetail, setShowMoreDetail] = useState(false);

  // Scroll to top when page loads
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [organId]);
  
  const anatomy = getAnatomyPage(organId);
  
  if (!anatomy) {
    return (
      <PageWrapper>
        <div className="anatomy-not-found">
          <h2>Page Not Found</h2>
          <p>This anatomy page does not exist.</p>
          <button onClick={() => navigate('/anatomy')} className="btn-primary">
            View All Anatomy Pages
          </button>
        </div>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper>
      <button 
        onClick={() => navigate(-1)} 
        className="back-btn"
        data-testid="back-btn"
      >
        ← Back
      </button>

      <article className="anatomy-article" data-testid="anatomy-article">
        <header className="anatomy-header">
          <h1>{anatomy.title}</h1>
          <p className="anatomy-subtitle">{anatomy.subtitle}</p>
        </header>

        {/* Anatomy Diagrams - Multiple Images */}
        {anatomy.images && anatomy.images.length > 0 && (
          <div className="anatomy-images-gallery" data-testid="anatomy-images">
            {anatomy.images.map((img, index) => (
              <div key={index} className="anatomy-image-container">
                <img 
                  src={img.url} 
                  alt={img.alt}
                  className="anatomy-diagram"
                  loading={index === 0 ? "eager" : "lazy"}
                />
                <p className="anatomy-image-caption">
                  <em>{img.caption}</em>
                </p>
                <p className="anatomy-image-source">
                  Source: {img.source}
                </p>
              </div>
            ))}
          </div>
        )}

        {/* Simple Explanation */}
        <section className="anatomy-section">
          <h2>What Is It?</h2>
          <p className="anatomy-explanation">{anatomy.simpleExplanation}</p>
        </section>

        {/* Quick Facts */}
        <section className="anatomy-section anatomy-facts">
          <h2>Quick Facts</h2>
          <div className="facts-grid">
            <div className="fact-item">
              <span className="fact-label">Location</span>
              <span className="fact-value">{anatomy.location}</span>
            </div>
            <div className="fact-item">
              <span className="fact-label">Size</span>
              <span className="fact-value">{anatomy.size}</span>
            </div>
            <div className="fact-item">
              <span className="fact-label">Function</span>
              <span className="fact-value">{anatomy.function}</span>
            </div>
          </div>
        </section>

        {/* Common Conditions */}
        <section className="anatomy-section">
          <h2>Common Conditions</h2>
          <div className="conditions-list">
            {anatomy.commonConditions.map((condition, index) => (
              <div key={index} className="condition-item">
                <h3>{condition.name}</h3>
                <p>{condition.description}</p>
                {condition.whenToSeekCare && (
                  <UrgencyGuidance 
                    emergency={condition.whenToSeekCare.emergency}
                    urgent={condition.whenToSeekCare.urgent}
                    routine={condition.whenToSeekCare.routine}
                  />
                )}
                
                {/* Cross-Links to Articles and Videos */}
                {condition.links && (
                  <div className="condition-links">
                    {condition.links.articles && condition.links.articles.length > 0 && (
                      <div className="condition-links-section">
                        <span className="condition-links-label">
                          <FileText size={14} /> Learn More:
                        </span>
                        <div className="condition-links-list">
                          {condition.links.articles.map((article, idx) => (
                            <Link 
                              key={idx} 
                              to={article.path} 
                              className="condition-link"
                              data-testid={`condition-link-article-${index}-${idx}`}
                            >
                              {article.title}
                              <ExternalLink size={12} />
                            </Link>
                          ))}
                        </div>
                      </div>
                    )}
                    {condition.links.videos && condition.links.videos.length > 0 && (
                      <div className="condition-links-section">
                        <span className="condition-links-label">
                          <Play size={14} /> Related Videos:
                        </span>
                        <div className="condition-links-list">
                          {condition.links.videos.map((video, idx) => (
                            <Link 
                              key={idx} 
                              to="/videos" 
                              className="condition-link condition-link-video"
                              data-testid={`condition-link-video-${index}-${idx}`}
                            >
                              {video.title}
                              <ExternalLink size={12} />
                            </Link>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>

        {/* More Detail (Expandable) */}
        {anatomy.moreDetail && (
          <section className="anatomy-section">
            <button 
              className="more-detail-toggle"
              onClick={() => setShowMoreDetail(!showMoreDetail)}
              aria-expanded={showMoreDetail}
            >
              <span>More Detail</span>
              {showMoreDetail ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
            </button>
            {showMoreDetail && (
              <div className="more-detail-content">
                <p>{anatomy.moreDetail}</p>
              </div>
            )}
          </section>
        )}

        {/* Disclaimer */}
        <div className="anatomy-disclaimer">
          <AlertTriangle size={16} />
          <p>
            <strong>Educational purposes only.</strong> This information is not medical advice. 
            Always consult your doctor about your specific condition.
          </p>
        </div>
      </article>
    </PageWrapper>
  );
};

// Anatomy Index Page - lists all anatomy pages
export const AnatomyIndexPage = () => {
  const anatomyList = getAllAnatomyPages();

  return (
    <PageWrapper>
      <div className="anatomy-index" data-testid="anatomy-index">
        <header className="page-header-simple">
          <h2>Anatomy Guide</h2>
          <p>Learn about the parts of your urinary and reproductive system</p>
        </header>

        <div className="anatomy-grid">
          {anatomyList.map((item) => (
            <Link 
              key={item.id} 
              to={`/anatomy/${item.id}`}
              className="anatomy-card"
              data-testid={`anatomy-card-${item.id}`}
            >
              <BookOpen size={28} className="anatomy-card-icon" />
              <h3>{item.title}</h3>
              <p>{item.subtitle}</p>
            </Link>
          ))}
        </div>

        <div className="anatomy-disclaimer">
          <AlertTriangle size={16} />
          <p>
            <strong>Educational purposes only.</strong> This information is not medical advice.
          </p>
        </div>
      </div>
    </PageWrapper>
  );
};

export default AnatomyPage;
